import type { Messages } from '../i18n/messages';

/**
 * Search module message catalog (Task 7i of the Fun Mode string sweep). Every
 * static chrome string in packages/app/src/modules/search/* is keyed here.
 * Server/dynamic data (result titles/snippets/urls, entity kinds/owners, the
 * user's query text, counts) is never keyed — only interpolated via `{param}`.
 */
export const searchMessages: Messages = {
  // ---- SearchPage.tsx (page header) ----
  'search.page.title': { en: 'Search', fun: 'Go Fish' },
  'search.page.description': {
    en: 'Search across the software catalog and TechDocs.',
    fun: 'Dig through the software catalog and TechDocs.',
  },

  // ---- Result-type labels (searchModel.ts resultTypeKey, shared by
  // SearchPage's Type facet, SearchPalette's group headers and
  // SearchResultItem's badge) ----
  'search.resultType.softwareCatalog': {
    en: 'Software Catalog',
    fun: 'Catalog Stash',
  },
  'search.resultType.techdocs': { en: 'TechDocs', fun: 'Docs Pile' },

  // ---- Shared across SearchPalette + SearchResultsView ----
  'search.common.searching': { en: 'Searching…', fun: 'Sniffing around…' },

  // ---- SearchPalette.tsx (command palette) ----
  'search.palette.ariaLabel': { en: 'Search', fun: 'Go Fish' },
  'search.palette.shortcut': {
    en: 'Open anytime with Cmd/Ctrl + K',
    fun: 'Open anytime with Cmd/Ctrl + K',
  },
  'search.palette.placeholder': {
    en: 'Search the catalog and docs…',
    fun: 'What are we hunting for…',
  },
  'search.palette.emptyTitleNoResults': { en: 'No results', fun: 'Nada' },
  'search.palette.emptyTitleStart': {
    en: 'Start typing to search',
    fun: 'Start typing, see what turns up',
  },
  'search.palette.emptyDescriptionNoResults': {
    en: 'Try a different query.',
    fun: 'Try a different query — this one came up dry.',
  },
  'search.palette.emptyDescriptionStart': {
    en: 'Search catalog entities and docs.',
    fun: 'Hunt through catalog entities and docs.',
  },
  'search.palette.viewAllFor': {
    en: 'View all results for "{term}"',
    fun: 'See everything for "{term}"',
  },
  'search.palette.viewAll': {
    en: 'View all results',
    fun: 'See everything',
  },

  // ---- SearchResultsView.tsx (full results page) ----
  'search.results.facet.type': { en: 'Type', fun: 'Flavor' },
  'search.results.facet.kind': { en: 'Kind', fun: 'Kind' },
  'search.results.facet.owner': { en: 'Owner', fun: 'Whose stuff?' },
  'search.results.facet.lifecycle': { en: 'Lifecycle', fun: 'Lifecycle' },
  'search.results.facet.all': { en: 'All', fun: 'Everything' },
  'search.results.facet.typePlaceholder': {
    en: 'All types',
    fun: 'Any flavor',
  },
  'search.results.facet.typeSearchPlaceholder': {
    en: 'Search types…',
    fun: 'Hunt for a flavor…',
  },
  'search.results.facet.ownerPlaceholder': {
    en: 'All owners',
    fun: 'Anybody’s stuff',
  },
  'search.results.facet.ownerSearchPlaceholder': {
    en: 'Search owners…',
    fun: 'Hunt for an owner…',
  },
  'search.results.input.ariaLabel': { en: 'Search', fun: 'Go Fish' },
  'search.results.input.placeholder': { en: 'Search…', fun: 'Hunt for…' },
  'search.results.count.singular': { en: 'result', fun: 'catch' },
  'search.results.count.plural': { en: 'results', fun: 'catches' },
  'search.results.empty.title': { en: 'No results', fun: 'Nada' },
  'search.results.empty.description': {
    en: 'Try a different query or clear the filters.',
    fun: 'Came up empty — try a different query or clear the filters.',
  },
  'search.results.pagination.ariaLabel': {
    en: 'Pagination',
    fun: 'Pagination',
  },
  'search.results.pagination.previous': { en: 'Previous', fun: 'Back it up' },
  'search.results.pagination.next': { en: 'Next', fun: 'Onward' },
  'search.results.pagination.page': {
    en: 'Page {pageIndex}',
    fun: 'Page {pageIndex}',
  },
};
